import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Shield, Clock, Package, FileText, Truck, CheckCircle, AlertTriangle } from 'lucide-react';
import { Card } from '../components/ui/card';

export default function WarrantyPage() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <>
      <Header />
      <main className="pt-20">
        {/* Hero */}
        <section className="bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 rounded-xl mb-6">
              <Shield className="text-white" size={32} />
            </div>
            <h1 data-testid="warranty-title" className="text-4xl sm:text-5xl font-bold mb-4">
              Política de Garantia
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Na SSA DISTRIBUIDORA, entendemos que a continuidade da sua linha de produção depende da qualidade de cada componente. Nossa política de garantia é pautada pela transparência e pelo compromisso com o padrão MRO.
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-12">

          {/* 1. Prazos de Garantia */}
          <section data-testid="warranty-section-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-red-600 rounded-lg text-white font-bold">1</div>
              <h2 className="text-2xl font-bold text-gray-900">Prazos de Garantia</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Todos os produtos comercializados pela SSA DISTRIBUIDORA possuem garantia contra defeitos de fabricação, seguindo os critérios abaixo:
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="p-6 border-t-4 border-t-red-600">
                <Clock className="text-red-600 mb-3" size={24} />
                <h3 className="font-bold text-gray-900 mb-2">Garantia Legal</h3>
                <p className="text-sm text-gray-600">90 dias conforme o Código de Defesa do Consumidor.</p>
              </Card>
              <Card className="p-6 border-t-4 border-t-gray-700">
                <Package className="text-gray-700 mb-3" size={24} />
                <h3 className="font-bold text-gray-900 mb-2">Garantia Estendida (Curva A e B)</h3>
                <p className="text-sm text-gray-600">Para itens críticos e de alto giro, repassamos integralmente o prazo oferecido pelo fabricante (6 meses a 2 anos).</p>
              </Card>
              <Card className="p-6 border-t-4 border-t-gray-700">
                <FileText className="text-gray-700 mb-3" size={24} />
                <h3 className="font-bold text-gray-900 mb-2">Produtos sob Encomenda (Curva C)</h3>
                <p className="text-sm text-gray-600">A garantia será especificada formalmente no momento da cotação e fechamento do pedido.</p>
              </Card>
            </div>
          </section>

          {/* 2. Abrangência da Garantia */}
          <section data-testid="warranty-section-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-red-600 rounded-lg text-white font-bold">2</div>
              <h2 className="text-2xl font-bold text-gray-900">Abrangência da Garantia</h2>
            </div>
            <Card className="p-6">
              <p className="text-gray-700 mb-4">
                A garantia cobre exclusivamente <strong>defeitos de fabricação</strong> e falhas de material que ocorram sob condições normais de uso e instalação técnica adequada.
              </p>
              <div className="bg-amber-50 border-l-4 border-l-amber-500 p-4 rounded-r-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                  <p className="text-sm text-amber-800">
                    <strong>Importante:</strong> A garantia não se aplica a danos causados por erro de especificação técnica pelo cliente, montagem incorreta, falta de manutenção preventiva ou desgaste natural em condições severas fora do padrão do fabricante.
                  </p>
                </div>
              </div>
            </Card>
          </section>

          {/* 3. Procedimento de RMA */}
          <section data-testid="warranty-section-3">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-red-600 rounded-lg text-white font-bold">3</div>
              <h2 className="text-2xl font-bold text-gray-900">Procedimento de RMA (Retorno de Mercadoria)</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Para agilizar o atendimento e minimizar o tempo de máquina parada na sua empresa, adotamos o seguinte fluxo:
            </p>
            <div className="space-y-4">
              {[
                { title: 'Abertura de Chamado', desc: 'Envie um e-mail para garantia@ssadistribuidora.com.br e nossa equipe de Operações revisará a cópia da NF-e e fotos/vídeos evidenciando a falha.' },
                { title: 'Triagem Técnica', desc: 'Nossa equipe realizará uma análise preliminar em até 48 horas úteis.' },
                { title: 'Coleta/Envio', desc: 'Após autorização, o produto deverá ser enviado para nossa unidade em Duque de Caxias/RJ com a NF de Remessa para Conserto/Troca.' },
                { title: 'Laudo Final', desc: 'O prazo para o laudo técnico definitivo é de até 30 dias (conforme legislação), mas buscamos priorizar itens de Curva A e B para reduzir o impacto operacional do cliente.' },
              ].map((step, i) => (
                <Card key={i} className="p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-bold text-gray-700">
                    {i + 1}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">{step.title}</h4>
                    <p className="text-sm text-gray-600">{step.desc}</p>
                  </div>
                </Card>
              ))}
            </div>
          </section>

          {/* 4. Trocas e Devoluções */}
          <section data-testid="warranty-section-4">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-red-600 rounded-lg text-white font-bold">4</div>
              <h2 className="text-2xl font-bold text-gray-900">Trocas e Devoluções</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-6 border-l-4 border-l-red-600">
                <h3 className="font-bold text-gray-900 mb-2">Erro de Envio</h3>
                <p className="text-sm text-gray-600">Caso receba um item em desacordo com o pedido, a SSA arcará com todos os custos logísticos de troca imediata.</p>
              </Card>
              <Card className="p-6 border-l-4 border-l-gray-700">
                <h3 className="font-bold text-gray-900 mb-2">Arrependimento (B2B)</h3>
                <p className="text-sm text-gray-600">Trocas por erro de compra por parte do cliente serão analisadas individualmente, podendo incidir taxas de reestoque (restocking fee), especialmente para itens de Curva C (sob encomenda).</p>
              </Card>
            </div>
          </section>

          {/* 5. Por que a SSA */}
          <section data-testid="warranty-section-5" className="bg-gray-50 rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Por que a SSA DISTRIBUIDORA é o parceiro ideal para sua operação?</h2>
            <p className="text-gray-600 mb-8">
              No setor de manutenção preventiva e corretiva, uma peça barata que falha custa muito mais caro do que uma peça de qualidade. Na SSA, nossa garantia não é apenas um documento, mas um compromisso com a sua produtividade.
            </p>
            <div className="space-y-6">
              {[
                { icon: CheckCircle, title: 'Rastreabilidade e Procedência Inquestionável', desc: 'Não operamos com "mercado cinza". 100% do nosso estoque provém de fabricantes homologados ou canais oficiais. Isso garante que cada componente possui certificação de origem e suporte direto da fábrica.' },
                { icon: Shield, title: 'Engenharia de Vendas: Consultoria antes da Nota Fiscal', desc: 'Atuamos com atendimento técnico-comercial para validar sua demanda. Ao entender a aplicação final, reduzimos drasticamente o índice de falhas por erro de especificação, garantindo que a garantia quase nunca precise ser acionada.' },
                { icon: Truck, title: 'Gestão de Crise e Agilidade Logística (Eixo RJ-SP)', desc: 'Sabemos que "máquina parada é prejuízo". Nossa localização estratégica na Rodovia Washington Luiz nos permite uma resposta logística superior. Em casos críticos de garantia para itens de Curva A, nossa prioridade é a retomada da sua operação.' },
                { icon: FileText, title: 'Transparência Comercial (Curva ABC)', desc: 'Nossa política de preços é racional e baseada em valor real. Mantemos margens sustentáveis para garantir que, quando você precisar de suporte ou pós-venda, nossa estrutura estará pronta e sólida para atendê-lo.' },
                { icon: Package, title: 'Foco Exclusivo em MRO Industrial', desc: 'Respiramos a rotina da indústria. Nossa linguagem, nossos prazos e nossa política de garantia são desenhados para atender às exigências de auditores e gestores de manutenção que não podem abrir mão da conformidade técnica.' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                      <Icon className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

        </div>
      </main>
      <Footer />
    </>
  );
}
